import Component from '@glimmer/component';
import Chip from 'tio-reimagined/components/chip';
import edjsHTML from 'editorjs-html';
import pageTitle from 'ember-page-title/helpers/page-title';
import { Section, VStack } from 'tio-ui/components/layout';
import { PhGlobe, PhCaretRight } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type PartnerService from 'tio-employee/services/partner';
import type LpnPage from 'tio-common/models/lpn-page';
import type EnrichedContent from 'tio-common/models/enriched-content';
import type CompanySettingModel from 'tio-common/models/company-setting';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type CompanyLpnSetting from 'tio-common/models/company-lpn-setting';

interface LpnShowComponentArgs {
  model: {
    lpnPage: LpnPage;
    enrichedContent: EnrichedContent;
    companySettings: CompanySettingModel;
    companyLpnSettings: CompanyLpnSetting | null;
  };
}

export default class LpnShowComponent extends Component<LpnShowComponentArgs> {
  @service declare partner: PartnerService;
  @service declare store: StoreService;

  get lpnPage() {
    return this.args.model.lpnPage;
  }

  get isCompanyCreated() {
    return this.args.model.lpnPage.isCompanyCreated;
  }

  get companyLpnSettings() {
    return this.args.model.companyLpnSettings;
  }

  get isEmployerRecommended() {
    if (this.isCompanyCreated) {
      return true;
    }
    return this.companyLpnSettings?.employerRecommended || false;
  }

  get hasCappedTuition() {
    if (this.isCompanyCreated) {
      return this.lpnPage.badgeCappedTuition;
    }
    return this.companyLpnSettings?.cappedTuition || false;
  }

  get hasDiscount() {
    if (this.args.model.lpnPage.badgeDiscount && this.args.model.lpnPage.discountPercent) {
      return true;
    }
    return this.companyLpnSettings?.hasDiscount || false;
  }

  get calculatedDiscount() {
    if (!this.hasDiscount) {
      return;
    }

    let discountPercent;
    if (this.companyLpnSettings?.hasDiscount) {
      discountPercent = this.companyLpnSettings.discountPercent;
    } else {
      discountPercent = this.args.model.lpnPage.discountPercent;
    }

    return {
      class: '!bg-violet-100 !text-violet-800',
      text: `Save ${discountPercent}%`,
    };
  }

  get badges() {
    let list = [];
    if (this.hasCappedTuition) {
      list.push({
        class: '!bg-violet-600 !text-white !ring-violet-600/10',
        text: 'No Out-of-Pocket $',
      });
    }
    if (this.hasDiscount) {
      list.push(this.calculatedDiscount);
    }
    if (this.args.model.lpnPage.badgeFamilySavings) {
      list.push({
        class: '!bg-violet-50 !text-violet-800',
        text: 'Family Savings',
      });
    }

    if (this.args.model.lpnPage.badgeFeesWaived) {
      list.push({
        class: '!bg-white !ring-violet-100 !text-violet-800 !border !border-violet-100',
        text: 'Fees Waived',
      });
    }

    return list;
  }

  get enrichedContent() {
    const edjsParser = edjsHTML();
    let html = edjsParser.parse(this.args.model.enrichedContent?.content);
    return html;
  }

  get description() {
    const edjsParser = edjsHTML();
    let html = edjsParser.parse(this.args.model.lpnPage.description);
    return html;
  }

  get safeUrl() {
    const url = this.args.model.lpnPage.url || '';
    return /^(https?:\/\/)/.test(url) ? url : `https://${url}`;
  }

  <template>
    {{pageTitle @model.lpnPage.schoolName}}
    <VStack class="flex min-h-full flex-col p-4">
      {{#if @model.lpnPage}}
        <span class="flex items-center text-gray-600">
          <PhGlobe @size="24" class="mr-2" />
          <LinkTo @route="authenticated.lpn.index" class="underline">{{t
              "lpn.learning_partners"
            }}</LinkTo>
          <PhCaretRight class="ml-2 text-gray-600" />
          <p class="ml-2">{{t "lpn.partner_details"}}</p>
        </span>
        <div class="flex items-center">
          <h2 class="text-2xl font-semibold text-gray-600 mr-2">{{@model.lpnPage.schoolName}}</h2>
          {{#if this.isEmployerRecommended}}
            <Chip class="bg-[#00809d]! text-white! ring-[#00809d]/10!">
              <span class="text-xs">{{t "lpn.employer_recommended"}}</span>
            </Chip>
          {{/if}}
        </div>
        <div class="flex items-center gap-x-2">
          {{#each this.badges as |badge|}}
            <Chip class={{badge.class}}>
              <span class="text-xs">{{badge.text}}</span>
            </Chip>
          {{/each}}
        </div>
        <Section>
          <:body>
            <div>
              <span class="flex items-center text-lg font-semibold text-gray-700 mb-4">
                <PhGlobe @size="24" class="text-purple-900 inline" />
                {{t "lpn.tio_learning_partner"}}
              </span>
            </div>
            <div class="lpn-page">
              {{#if @model.enrichedContent}}
                {{htmlSafe this.enrichedContent}}
              {{/if}}
              {{htmlSafe this.description}}
            </div>
            <div class="mt-4">
              <a
                class="font-bold text-primary-500 underline"
                href={{this.safeUrl}}
                target="_blank"
                rel="noopener noreferrer"
              >
                {{t "lpn.visit_partner" schoolName=@model.lpnPage.schoolName}}
              </a>
            </div>
          </:body>
        </Section>
      {{/if}}
    </VStack>
  </template>
}
